import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';

const defaultForm = {
  name: '',
  code: '',
  description: '',
  category: '',
  estimated_duration: '',
  status: 2,
  start_date: '',
  end_date: '',
  certificate_template_id: '',
  passing_percentage: 70,
  completion_criteria: 'all_mandatory',
  navigation_mode: 'sequential',
  lock_next_until_previous: true,
  require_passing_assessment: true,
  require_course_completion: true,
  require_resource_view: true,
  require_vilt_attendance: true,
  thumbnail: null
};

const extractCertificateTemplates = (response) => {
  const payload = response?.data?.data ?? response?.data ?? {};
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload.templates) ? payload.templates : [];
};

const LearningJourneyForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(defaultForm);
  const [preview, setPreview] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const res = await api.certificates.getTemplates({ status: 'active', limit: 100 });
        setTemplates(extractCertificateTemplates(res));
      } catch {
        setTemplates([]);
      }
    };
    loadTemplates();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.learningJourneys.getById(id);
        const j = res.data?.data;
        if (!j) {
          toast.error('Journey not found');
          navigate('/admin/learning-journeys');
          return;
        }
        setForm({
          name: j.name || '',
          code: j.code || '',
          description: j.description || '',
          category: j.category || '',
          estimated_duration: j.estimated_duration || '',
          status: j.status ?? 2,
          start_date: j.start_date ? String(j.start_date).slice(0, 16) : '',
          end_date: j.end_date ? String(j.end_date).slice(0, 16) : '',
          certificate_template_id: j.certificate_template_id || '',
          passing_percentage: j.passing_percentage ?? 70,
          completion_criteria: j.completion_criteria || 'all_mandatory',
          navigation_mode: j.navigation_mode || 'sequential',
          lock_next_until_previous: !!j.lock_next_until_previous,
          require_passing_assessment: !!j.require_passing_assessment,
          require_course_completion: !!j.require_course_completion,
          require_resource_view: !!j.require_resource_view,
          require_vilt_attendance: !!j.require_vilt_attendance,
          thumbnail: null
        });
        if (j.thumbnail) setPreview(getAssetUrl(j.thumbnail));
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to load journey');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEdit, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      const file = files?.[0] || null;
      setForm((prev) => ({ ...prev, thumbnail: file }));
      if (file) setPreview(URL.createObjectURL(file));
      return;
    }
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const buildFormData = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === 'thumbnail') {
        if (value) fd.append('thumbnail', value);
        return;
      }
      if (typeof value === 'boolean') {
        fd.append(key, value ? '1' : '0');
        return;
      }
      if (value !== null && value !== undefined) {
        fd.append(key, value);
      }
    });
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) {
      toast.error('Journey name and code are required');
      return;
    }
    try {
      setSaving(true);
      const fd = buildFormData();
      if (isEdit) {
        await api.learningJourneys.update(id, fd);
        toast.success('Learning journey updated');
        navigate(`/admin/learning-journeys/${id}/builder`);
      } else {
        const res = await api.learningJourneys.create(fd);
        const newId = res.data?.data?.id;
        toast.success('Learning journey created');
        if (newId) navigate(`/admin/learning-journeys/${newId}/builder`);
        else navigate('/admin/learning-journeys');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save journey');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <Spinner animation="border" role="status" className="h-12 w-12 text-purple-600">
          <span className="visually-hidden">Loading learning journey...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-3 md:p-6 lg:p-8">
      <div className="mx-auto mb-4 max-w-6xl">
        <Row className="align-items-center g-3">
          <Col md={2}>
            <Button
              bsPrefix="custom-hamburger"
              onClick={() => navigate('/admin/learning-journeys')}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-purple-300 bg-white px-4 py-2 text-sm font-semibold text-purple-600 shadow-md transition-all duration-200 hover:bg-purple-50 hover:shadow-lg"
              aria-label="Back to learning journeys"
            >
              <FaArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
            </Button>
          </Col>
          <Col md={10}>
            <h5 className="m-0 text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 md:text-3xl">
              {isEdit ? 'Edit Learning Journey' : 'Create Learning Journey'}
            </h5>
            <p className="mb-0 mt-1 text-sm text-gray-600">Basic information, navigation rules and completion criteria</p>
          </Col>
        </Row>
      </div>

      <Form onSubmit={handleSubmit} className="mx-auto max-w-6xl rounded-2xl border border-purple-100 bg-white/95 p-4 shadow-2xl backdrop-blur-sm sm:p-6 [&_.form-control]:rounded-xl [&_.form-control]:border-purple-200 [&_.form-label]:font-semibold [&_.form-label]:text-gray-700 [&_.form-select]:rounded-xl [&_.form-select]:border-purple-200">
        <h6 className="fw-bold text-purple-700 mb-3">Basic Information</h6>
        <Row className="g-3 mb-5 rounded-2xl border border-slate-100 bg-slate-50/70 p-2 sm:p-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Journey Name *</Form.Label>
              <Form.Control name="name" value={form.name} onChange={handleChange} required aria-label="Journey name" />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Journey Code *</Form.Label>
              <Form.Control name="code" value={form.code} onChange={handleChange} required aria-label="Journey code" />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select name="status" value={form.status} onChange={handleChange} aria-label="Status">
                <option value={2}>Draft</option>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} name="description" value={form.description} onChange={handleChange} aria-label="Description" />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Category</Form.Label>
              <Form.Control name="category" value={form.category} onChange={handleChange} aria-label="Category" />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Estimated Duration</Form.Label>
              <Form.Control name="estimated_duration" value={form.estimated_duration} onChange={handleChange} placeholder="e.g. 8 hours" aria-label="Estimated duration" />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Passing Percentage</Form.Label>
              <Form.Control type="number" min={0} max={100} name="passing_percentage" value={form.passing_percentage} onChange={handleChange} aria-label="Passing percentage" />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <Form.Control type="datetime-local" name="start_date" value={form.start_date} onChange={handleChange} aria-label="Start date" />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control type="datetime-local" name="end_date" value={form.end_date} onChange={handleChange} aria-label="End date" />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Certificate Template</Form.Label>
              <Form.Select name="certificate_template_id" value={form.certificate_template_id} onChange={handleChange} aria-label="Certificate template">
                <option value="">Select certificate...</option>
                {(Array.isArray(templates) ? templates : []).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Completion Criteria</Form.Label>
              <Form.Select name="completion_criteria" value={form.completion_criteria} onChange={handleChange} aria-label="Completion criteria">
                <option value="all_mandatory">All mandatory steps</option>
                <option value="all_steps">All steps</option>
                <option value="percentage">Passing percentage</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Thumbnail</Form.Label>
              <Form.Control type="file" accept="image/*" onChange={handleChange} aria-label="Thumbnail upload" />
              {preview && (
                <img src={preview} alt="Journey thumbnail preview" className="mt-2 w-32 h-32 object-cover rounded-xl border" />
              )}
            </Form.Group>
          </Col>
        </Row>

        <h6 className="fw-bold text-purple-700 mb-3">Navigation Rules</h6>
        <Row className="g-3 mb-5 rounded-2xl border border-slate-100 bg-slate-50/70 p-2 sm:p-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Navigation Mode</Form.Label>
              <Form.Select name="navigation_mode" value={form.navigation_mode} onChange={handleChange} aria-label="Navigation mode">
                <option value="sequential">Sequential (Recommended)</option>
                <option value="free">Free Navigation</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={8} className="d-flex flex-column justify-content-end gap-2">
            <Form.Check
              type="switch"
              id="lock_next_until_previous"
              name="lock_next_until_previous"
              label="Lock next step until previous completed"
              checked={form.lock_next_until_previous}
              onChange={handleChange}
            />
            <Form.Check
              type="switch"
              id="require_passing_assessment"
              name="require_passing_assessment"
              label="Require passing assessment"
              checked={form.require_passing_assessment}
              onChange={handleChange}
            />
            <Form.Check
              type="switch"
              id="require_course_completion"
              name="require_course_completion"
              label="Require course completion"
              checked={form.require_course_completion}
              onChange={handleChange}
            />
            <Form.Check
              type="switch"
              id="require_resource_view"
              name="require_resource_view"
              label="Require resource view"
              checked={form.require_resource_view}
              onChange={handleChange}
            />
            <Form.Check
              type="switch"
              id="require_vilt_attendance"
              name="require_vilt_attendance"
              label="Require VILT meeting attendance"
              checked={form.require_vilt_attendance}
              onChange={handleChange}
            />
          </Col>
        </Row>

        <div className="flex flex-col justify-end gap-3 sm:flex-row">
          <Button
            bsPrefix="custom-hamburger"
            type="button"
            onClick={() => navigate('/admin/learning-journeys')}
            className="rounded-xl border-2 border-gray-300 bg-gray-100 px-6 py-2 font-semibold text-gray-700 shadow-md transition-all hover:border-gray-500 hover:bg-gray-200"
          >
            Cancel
          </Button>
          <Button
            bsPrefix="custom-hamburger"
            type="submit"
            className="rounded-xl border border-purple-600 bg-white px-6 py-2 font-semibold text-purple-600 shadow-md transition-all hover:bg-purple-50 hover:shadow-lg"
            disabled={saving}
          >
            {saving ? <Spinner size="sm" animation="border" /> : <><FaSave className="me-2" aria-hidden="true" />Save & Open Builder</>}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default LearningJourneyForm;
